/**
 * src/components/map/mapHoverLifecycle.ts
 *
 * The difference between glancing at something and choosing it.
 *
 * Two states, and conflating them is what makes map UIs infuriating:
 *
 *   **Transient hover.** The pointer is over something. A lightweight
 *   card appears, costs no network, follows the cursor, and goes away
 *   shortly after the pointer leaves. Nothing about it is worth
 *   preserving, because the visitor never asked for it.
 *
 *   **Pinned selection.** The visitor clicked. Now there is a panel,
 *   possibly a conversation in it, and possibly a balance being
 *   loaded. This must not vanish because the mouse moved — a chat
 *   session destroyed by an accidental mouseleave is a bug the user
 *   experiences as the application being broken. It closes when they
 *   close it, or on Escape, and not otherwise.
 *
 * A grace period sits between the pointer leaving and the transient
 * card disappearing, because the gap between a building and its own
 * tooltip is a few pixels wide and crossing it should not be punished.
 * The pointer entering the card cancels the dismissal entirely, which
 * is how a card with buttons on it can be clicked at all.
 *
 * The reducer is pure and the clock is an input, so every rule here is
 * testable without a map: this is timing logic, and timing logic that
 * is only checked by hand is timing logic that regresses.
 */

export interface HoverTarget<T> {
  /** Stable identity, so re-entering the same thing is not a new hover. */
  id: string;
  payload: T;
  /** Cursor position in map-container pixels. */
  point: { x: number; y: number };
}

export interface MapHoverState<T> {
  hovered: HoverTarget<T> | null;
  pinned: HoverTarget<T> | null;
  /** True while the pointer is over the card rather than the map. */
  pointerInCard: boolean;
  /** When the transient card should go, or null if it should stay. */
  dismissAt: number | null;
}

export type MapHoverEvent<T> =
  | { type: 'enter'; target: HoverTarget<T>; at: number }
  | { type: 'move'; point: { x: number; y: number } }
  | { type: 'leave'; at: number }
  | { type: 'cardEnter' }
  | { type: 'cardLeave'; at: number }
  | { type: 'tick'; at: number }
  | { type: 'pin'; target: HoverTarget<T> }
  | { type: 'unpin' }
  | { type: 'escape' };

/** Crossing from a building to its own tooltip should not dismiss it. */
export const HOVER_GRACE_MS = 260;
/** Leaving the card itself is more deliberate, so it needs less slack. */
export const CARD_GRACE_MS = 200;

export function initialHoverState<T>(): MapHoverState<T> {
  return { hovered: null, pinned: null, pointerInCard: false, dismissAt: null };
}

export function mapHoverReducer<T>(
  state: MapHoverState<T>,
  event: MapHoverEvent<T>,
): MapHoverState<T> {
  switch (event.type) {
    case 'enter': {
      // The pinned object does not also get a hover card: two cards for
      // one building is a bug, and the pinned one is the one carrying
      // the conversation. Hovering anything *else* still works, so a
      // visitor can keep a chat open and go on browsing.
      if (state.pinned && state.pinned.id === event.target.id) {
        return state.hovered || state.dismissAt !== null
          ? { ...state, hovered: null, dismissAt: null }
          : state;
      }
      return { ...state, hovered: event.target, dismissAt: null };
    }

    case 'move': {
      if (!state.hovered) return state;
      // Frozen once the pointer is on the card — a card that slid away
      // from under the cursor could never be clicked.
      if (state.pointerInCard) return state;
      return { ...state, hovered: { ...state.hovered, point: event.point } };
    }

    case 'leave': {
      if (!state.hovered) return state;
      if (state.pointerInCard) return state;
      return { ...state, dismissAt: event.at + HOVER_GRACE_MS };
    }

    case 'cardEnter':
      return { ...state, pointerInCard: true, dismissAt: null };

    case 'cardLeave':
      return { ...state, pointerInCard: false, dismissAt: event.at + CARD_GRACE_MS };

    case 'tick': {
      if (state.dismissAt === null || event.at < state.dismissAt) return state;
      return { ...state, hovered: null, dismissAt: null, pointerInCard: false };
    }

    case 'pin':
      // Level 2 begins here and nowhere else. A click is the only thing
      // in this file that is allowed to cause a network request.
      return {
        ...state,
        pinned: event.target,
        hovered: null,
        dismissAt: null,
        pointerInCard: false,
      };

    case 'unpin':
    case 'escape':
      if (!state.pinned) return state;
      return { ...state, pinned: null };

    default:
      return state;
  }
}
